import React, { useState, useEffect } from 'react';
import { 
  BadgeDollarSign, 
  Calendar, 
  CreditCard,
  RefreshCw,
  Loader2,
  Coins,
  CalendarDays
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatCard from "@/components/StatCard";
import Sidebar from "@/components/Sidebar";
import SubscriptionManager from "@/components/subscription/SubscriptionManager";
import { useAuth } from "@/components/auth/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { getUserCredits, getCreditHistory } from '@/services/creditService';
import { format } from 'date-fns';
import InvoiceList from "@/components/InvoiceList";

const Credits: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingCredits, setLoadingCredits] = useState(true);
  const [userCredits, setUserCredits] = useState<{
    total: number;
    used: number;
    balance: number;
    resetDate: string | null;
  }>({
    total: 0,
    used: 0,
    balance: 0,
    resetDate: null
  });
  const [subscription, setSubscription] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0); // Add a key to force re-render
  const [selectedInterval, setSelectedInterval] = useState<'month' | 'year'>('month');
  
  useEffect(() => {
    if (user) {
      fetchUserCreditsAndSubscription();
    }
    
    // Check for success/canceled query params (from Stripe redirect)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success')) {
      toast.success('Subscription updated successfully!');
      // Remove the query parameter from the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (urlParams.get('canceled')) {
      toast.error('Subscription update was canceled');
      // Remove the query parameter from the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [user, refreshKey]);
  
  const fetchUserCreditsAndSubscription = async () => {
    try {
      setLoadingCredits(true);
      
      if (!user?.id) {
        throw new Error('User not found');
      }
      
      // Get user credits (now organization-based)
      const userCreditData = await getUserCredits(user.id);
      
      // Get subscription data if it exists - prioritize trials!
      
      // First check for trial subscriptions
      const { data: trialSubscriptions, error: trialError } = await supabase
        .from('subscriptions')
        .select(`
          *,
          plan:plans(
            id,
            name,
            credits,
            price,
            interval
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'trialing')
        .order('created_at', { ascending: false });
      
      if (trialError) {
        // Error handled silently
      }
      
      // Fall back to active subscription if no trial found
      const { data: activeSubscriptions, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select(`
          *,
          plan:plans(
            id,
            name,
            credits,
            price,
            interval
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      
      if (subscriptionError) {
        // Error handled silently
      }
      
      // Priority: use trial if available, otherwise use active
      const latestSubscription = 
        (trialSubscriptions && trialSubscriptions.length > 0)
          ? trialSubscriptions[0]
          : (activeSubscriptions && activeSubscriptions.length > 0)
            ? activeSubscriptions[0]
            : null;
      
      // Get plan details with fallback values
      const planName = latestSubscription?.plan?.name || 'Basic';
      const planCredits = latestSubscription?.plan?.credits || userCreditData.balance;
      
      setUserCredits({
        total: planCredits,
        used: userCreditData.total_used || 0,
        balance: userCreditData.balance || 0,
        resetDate: latestSubscription?.current_period_end || null
      });
      
      setSubscription(latestSubscription);
    } catch (error) {
      toast.error('Failed to load credit information');
    } finally {
      setLoadingCredits(false);
    }
  };
  
  const handleSubscribe = async (planId: string) => {
    if (!user) {
      toast.error('You must be logged in to subscribe');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Create a checkout session using the Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { planId, userId: user.id }
      });
      
      if (error) {
        throw new Error(`Error creating checkout session: ${error.message}`);
      }
      
      if (data?.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create subscription');
    } finally {
      setIsLoading(false);
    }
  };
  
  const formatResetDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMMM d, yyyy');
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    fetchUserCreditsAndSubscription();
    toast.success("Refreshing subscription data...");
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1 overflow-y-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Credits</h2>
              <p className="text-muted-foreground">
                Manage your assessment credits and subscription
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <StatCard
              title="Current Plan"
              value={
                <div className="flex items-center gap-2">
                  <span>
                    {subscription?.plan?.name || 'Basic'} 
                    {subscription?.status === 'trialing' && (
                      <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                        Trial
                      </span>
                    )}
                  </span>
                  {subscription && subscription.plan?.name !== 'Basic' && (
                    <span className="text-base">
                      (<button 
                        className="text-roundone-purple hover:text-roundone-purple/80 font-medium"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!user) {
                            toast.error('You must be logged in to manage your subscription');
                            return;
                          }

                          try {
                            const { data, error } = await supabase.functions.invoke('create-customer-portal', {
                              body: { userId: user.id }
                            });

                            if (error) {
                              throw new Error(`Error creating customer portal session: ${error.message}`);
                            }

                            if (data?.url) {
                              window.location.href = data.url;
                            } else {
                              throw new Error('No portal URL received');
                            }
                          } catch (error: any) {
                            toast.error(error.message || 'Failed to open customer portal');
                          }
                        }}
                      >
                        Manage
                      </button>)
                    </span>
                  )}
                </div>
              }
              icon={<CreditCard className="h-4 w-4 text-muted-foreground" />}
            />
            <StatCard
              title="Available Credits"
              value={userCredits?.balance?.toString() || '0'}
              icon={<Coins className="h-4 w-4 text-muted-foreground" />}
            />
            <StatCard
              title="Next Billing Date"
              value={subscription ? formatResetDate(subscription.current_period_end) : '-'}
              icon={<CalendarDays className="h-4 w-4 text-muted-foreground" />}
            />
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-medium mb-2">Subscription Management</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Choose a plan that best suits your needs
            </p>

            <Tabs defaultValue="monthly" className="w-full" onValueChange={(value) => setSelectedInterval(value === 'monthly' ? 'month' : 'year')}>
              <div className="flex justify-center mb-4">
                <TabsList className="high-contrast-tabs">
                  <TabsTrigger value="monthly">Monthly</TabsTrigger>
                  <TabsTrigger value="yearly">Yearly</TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="monthly" className="mt-0">
                <div className="text-center mb-3">
                  <p className="tab-content-text text-sm">Regular monthly pricing</p>
                </div>
                <SubscriptionManager 
                  onSubscribe={handleSubscribe} 
                  currentSubscription={subscription}
                  onUpdateSuccess={() => {
                    setRefreshKey(prev => prev + 1);
                  }}
                  selectedInterval="month"
                />
              </TabsContent>
              
              <TabsContent value="yearly" className="mt-0">
                <div className="text-center mb-3">
                  <p className="green-highlight text-sm">Save up to 25% by paying yearly</p>
                </div>
                <SubscriptionManager 
                  onSubscribe={handleSubscribe} 
                  currentSubscription={subscription}
                  onUpdateSuccess={() => {
                    setRefreshKey(prev => prev + 1);
                  }}
                  selectedInterval="year"
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Add Invoice History Section */}
          <div className="mt-8">
            <h3 className="text-lg font-medium">Invoice History</h3>
            <p className="text-sm text-muted-foreground mb-4">
              View and download your past invoices
            </p>
            {user ? (
              <InvoiceList userId={user.id} />
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                Please log in to view your invoices
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Credits;
